/**
 * Header density — how much vertical room the program header stack takes.
 *
 * Reported by Jon 2026-08-20 (DEV-660): on a short laptop window the six
 * stacked bands above a program's content eat about a third of the viewport,
 * and because every band sits outside the scroll container, scrolling never
 * wins that space back.
 *
 * The trigger is viewport *height*, so this deliberately does not use a
 * Tailwind `md:`/`lg:` breakpoint — those key off width, and a short window at
 * full width sails straight past them. The single source of truth is the
 * height media query below; every condensed style in the shell reads the
 * `condensed` boolean this context publishes rather than re-deriving it.
 *
 * An explicit user choice outranks the automatic default and is persisted, so
 * it survives both navigation (which remounts the shell when the section
 * changes) and reload.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Below this height the stack is condensed by default.
 *
 * 900px clears the common short-laptop cases — a 1366x768 or 1440x900 panel
 * leaves roughly 700-800px of viewport once browser chrome is subtracted, and
 * Jon's report was 764px — while leaving a maximized 1080p window (~950px of
 * viewport) comfortable.
 */
export const SHORT_VIEWPORT_QUERY = '(max-height: 900px)';

export const HEADER_DENSITY_STORAGE_KEY = 'header_density';

/**
 * `auto` tracks the viewport; the other two are explicit user overrides that
 * pin the header regardless of window size.
 */
export type DensityPreference = 'auto' | 'condensed' | 'comfortable';

const PREFERENCES: readonly DensityPreference[] = ['auto', 'condensed', 'comfortable'];

export interface HeaderDensity {
  /** Whether the header stack renders condensed right now. */
  condensed: boolean;
  /** The stored preference behind that decision. */
  preference: DensityPreference;
  /** Flip between condensed and comfortable, pinning and persisting the choice. */
  toggle: () => void;
}

const HeaderDensityContext = createContext<HeaderDensity>({
  condensed: false,
  preference: 'auto',
  toggle: () => {},
});

/** Read the stored preference, tolerating absent or unrecognised values. */
const readPreference = (): DensityPreference => {
  try {
    const stored = localStorage.getItem(HEADER_DENSITY_STORAGE_KEY);
    return PREFERENCES.includes(stored as DensityPreference)
      ? (stored as DensityPreference)
      : 'auto';
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). The
    // height-based default still works, so this is not worth failing over.
    return 'auto';
  }
};

/**
 * Tracks the height media query. Guards `matchMedia` because jsdom does not
 * implement it; a comfortable header is the safe fallback.
 */
const useIsShortViewport = (): boolean => {
  const [isShort, setIsShort] = useState(
    () => typeof matchMedia === 'function' && matchMedia(SHORT_VIEWPORT_QUERY).matches,
  );

  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mql = matchMedia(SHORT_VIEWPORT_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsShort(e.matches);
    setIsShort(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isShort;
};

/**
 * Owns density state. `AppShell` calls this and publishes the result, so the
 * shell can style its own chrome from the same value it hands to descendants
 * (a component cannot consume a context it provides in the same render).
 */
export const useHeaderDensityState = (): HeaderDensity => {
  const [preference, setPreference] = useState<DensityPreference>(readPreference);
  const isShort = useIsShortViewport();

  const condensed = preference === 'auto' ? isShort : preference === 'condensed';

  const toggle = useCallback(() => {
    setPreference((current) => {
      const resolved =
        current === 'auto'
          ? typeof matchMedia === 'function' && matchMedia(SHORT_VIEWPORT_QUERY).matches
          : current === 'condensed';
      const next: DensityPreference = resolved ? 'comfortable' : 'condensed';
      try {
        localStorage.setItem(HEADER_DENSITY_STORAGE_KEY, next);
      } catch {
        // Preference is lost on reload but the session still honours it.
      }
      return next;
    });
  }, []);

  return useMemo(() => ({ condensed, preference, toggle }), [condensed, preference, toggle]);
};

export const HeaderDensityProvider = ({ children }: { children: ReactNode }) => {
  const value = useHeaderDensityState();
  return <HeaderDensityContext.Provider value={value}>{children}</HeaderDensityContext.Provider>;
};

/**
 * Read the current density. Page-level chrome (e.g. MarketView's toolbar and
 * filter rows) uses this to decide whether to pin itself above the scroll
 * container or scroll away with the content.
 */
export const useHeaderDensity = (): HeaderDensity => useContext(HeaderDensityContext);

export { HeaderDensityContext };
